import { Deserializer } from 'jsonapi-serializer';
import { DeviceImproxWrapper } from '../models/DeviceImprox';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { APIConnector } from './api/APIConnector';
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';

const apiConnector = new APIConnector();

export const getDeviceImproxNames = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `deviceImprox?${paginationString}&include=site`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject(err);
                }

                const devices: Name[] = items.map((device): Name => {
                    return {
                        value: device['id'],
                        label: device['dev-name'],
                        optionalColumns: [{ key: 'site', value: device['site']?.['site-name'] }]
                    };
                });

                return resolve({ rows: devices, pagination: pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getDeviceImproxNamesWithPagnation = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `deviceImprox?fields[controller]=devName&fields[terminal]=devName${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject(err);
                }

                const devices: Name[] = items.map((device): Name => {
                    return {
                        value: device['id'],
                        label: device['dev-name']
                    };
                });

                return resolve({ rows: devices, pagination: newPagination });
            })
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}


export const getDeviceImprox = async (listParameters?: ListQuery): Promise<DeviceImproxWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const url = "deviceImprox?include=site,unitType" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject(error);
                }

                const devices: any[] = data.map((device) => {
                    return {
                        id: device['id'],
                        name: device['dev-name'],
                        nameOriginal: device['dev-name'],
                        site: device['site']?.['site-name'],
                        unitType: device['unit-type']?.['trn-name'],
                        fixedAddress: device['dev-fixed-addr'],
                        ipAddress: device['dev-ip-address'],
                    };
                });

                return resolve({ rows: devices, pagination })
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}